import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";
import { addApiError, refreshPage, removeApiError } from "src/store/errorBoundarySlice";
import store from "../store/store";
import authService from "src/services/authService";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import { v4 as uuidv4 } from "uuid";
import { setShowBatchErrorMessageBanner } from "src/store/batchExtractionSlice";

const apiClient = axios.create({
  baseURL: "https://beta.na.excavator-api.whs.amazon.dev",
});

const SHOW_BATCH_PROCESS_EXCEED_DEFAULT_LIMIT_BANNER_ERROR_MESSAGE =
  "Error in validate upload batch file: File exceeds maximum limit of 50 lines";

axiosRetry(apiClient, {
  retries: 1, // number of retries
  retryDelay: (retryCount) => {
    console.log(`retry attempt: ${retryCount}`);
    // use store.dispatch(getUser()) will make retry instantly, which cause more chance to having race condition
    authService.login();
    return retryCount * 2000; // time interval between retries
  },
  retryCondition: (error: AxiosError) => {
    // if retry condition is not specified, by default idempotent requests are retried
    const status = error.response?.status;
    if (!status || status != 401) {
      return false;
    }
    return true;
  },
});

// Request interceptor to clear previous errors for the same endpoint
apiClient.interceptors.request.use(
  (config) => {
    // Clear any previous error for this endpoint when making a new request
    if (config.url) {
      store.dispatch(removeApiError(config.url));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 403) {
      store.dispatch(
        createToast({
          type: "error",
          message: "Permission denied. Redirecting to landing page...",
          timeout: TOAST_TIMEOUT,
        }),
      );
      setTimeout(() => {
        store.dispatch(refreshPage());
      }, 3000);
      return Promise.reject(error);
    } else if (error.response) {
      // we should consider scale this if we see more similar pattern
      if (
        error.response?.data.detail.includes(
          SHOW_BATCH_PROCESS_EXCEED_DEFAULT_LIMIT_BANNER_ERROR_MESSAGE,
        )
      ) {
        store.dispatch(setShowBatchErrorMessageBanner(true));
        // default behavior: dispatch toast
      } else {
        store.dispatch(
          createToast({
            type: "error",
            message: error.response?.data.detail,
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
      store.dispatch(
        addApiError({
          key: error.response.config.url,
          value: error.response?.data.detail,
        }),
      );
    }
  },
);

export default apiClient;
